import React, { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { FiChevronRight } from "react-icons/fi";
import { useTitle } from "../../../hooks/useTitle";
import { useAuth } from "../../../services/useAuth";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Pagination from "../../../components/BaseUI/Pagination";
import Select from "../../../components/BaseUI/Select";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import { buildAdaptiveSingleFilterSection } from "../shared/buildListFilterSections";
import "./ProductionCapacity.scss";

// No more mockData, data is fetched from API

const tableColumns = [
  { name: "Makine", id: 1, sortable: false },
  { name: "Parça", id: 2, sortable: false },
  { name: "Üretim", id: 3, sortable: false },
];

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ProductionCapacity = () => {
  useTitle("Rovion MES - Üretim Kapasitesi");
  const { getToken } = useAuth();

  const [expandedRows, setExpandedRows] = useState({});
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [computedData, setComputedData] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken]
  );

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const shiftRes = await axios.get(`${API_URL}/api/shifts`, authHeaders());
        if (shiftRes.data?.shifts) {
          const shiftOptions = shiftRes.data.shifts.map((s) => {
            const firstSchedule = s.daySchedules && s.daySchedules[0];
            const timeStr = firstSchedule ? ` (${firstSchedule.startTime} - ${firstSchedule.endTime})` : '';
            return {
              value: s._id,
              label: `${s.name}${timeStr}`,
            };
          });
          setShifts(shiftOptions);
          if (shiftOptions.length > 0 && !selectedShift) {
            setSelectedShift(shiftOptions[0].value);
          }
        }
      } catch (err) {
        console.error("Vardiyalar yüklenemedi", err);
      }
    };
    fetchShifts();
  }, [authHeaders]); // removed selectedShift from dep array intentionally to only set once on load

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedShift) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/reports/production-capacity`, {
          ...authHeaders(),
          params: { shiftId: selectedShift }
        });
        if (res.data?.success) {
          setComputedData(res.data.data);
          setExpandedRows({});
        }
      } catch (err) {
        console.error("Kapasite verisi yüklenemedi", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedShift, authHeaders]);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return computedData.slice(start, start + itemsPerPage);
  }, [computedData, currentPage, itemsPerPage]);

  const grandTotal = useMemo(() => {
    return computedData.reduce((sum, item) => sum + item.totalCapacity, 0);
  }, [computedData]);

  return (
    <AppPage 
      title="Üretim Kapasitesi"
      actions={
        <ListViewOptionsPanel
          filterSections={[
            buildAdaptiveSingleFilterSection({
              id: "shift",
              title: "Vardiya",
              value: selectedShift,
              onChange: setSelectedShift,
              options: shifts,
              allValue: "",
              allLabel: "Seçiniz"
            })
          ]}
          activeFilterCount={selectedShift ? 1 : 0}
          onReset={() => setSelectedShift(shifts?.[0]?.value || null)}
          showApply={false}
          showReset={false}
        />
      }
    >
      <CardContainer className="d-flex flex-column h-100">
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Table
            columns={tableColumns}
            search={false}
            hideFooter={true}
            loading={loading}
            className="capacity-table-container"
          >
            {paginatedData.map((parent) => {
              const isExpanded = !!expandedRows[parent.id];

            return (
              <React.Fragment key={parent.id}>
                {/* Parent Row */}
                <Tr
                  className={`capacity-parent-row ${
                    isExpanded ? "expanded" : ""
                  }`}
                  onClick={() => toggleExpand(parent.id)}
                >
                  <Td>
                    <div className="parent-title">
                      <span
                        className={`expand-icon ${
                          isExpanded ? "rotated" : ""
                        }`}
                      >
                        <FiChevronRight size={18} />
                      </span>
                      {parent.machineName}
                      {parent.machineCode && (
                        <span className="badge">{parent.machineCode}</span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="parent-subtext">
                      {parent.partsCount} parça · {parent.variantsCount}{" "}
                      varyant
                    </div>
                  </Td>
                  <Td>
                    {/* <div className="parent-total">{parent.totalCapacity}</div> */}
                  </Td>
                </Tr>

                {/* Child Rows */}
                {isExpanded &&
                  parent.children.map((child) => (
                    <Tr key={child.id} className="capacity-child-row">
                      <Td></Td>
                      <Td>
                        <div className="child-machine">
                          {child.partDesc}
                          {child.partCode && (
                            <span className="machine-badge">
                              {child.partCode}
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="child-capacity">{child.capacity}</div>
                      </Td>
                    </Tr>
                  ))}
              </React.Fragment>
            );
          })}
        </Table>
        </div>
        <div style={{ marginTop: "1rem", flexShrink: 0 }}>
          <Pagination
            totalItems={computedData.length}
            totalPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={({ pagination }) => {
              setCurrentPage(pagination.currentPage);
              setItemsPerPage(pagination.itemsPerPage);
            }}
          />
        </div>
      </CardContainer>
    </AppPage>
  );
};

export default ProductionCapacity;
